import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TrendBron } from '../hooks/useWekenoverzicht';
import { MaandOverzicht } from './MaandOverzicht';

type Weergave = 'week' | 'maand';
const WEERGAVEN: readonly Weergave[] = ['week', 'maand'];

/** Standaard 3 maanden (besluit 14-10 #5, 20-10 #11). */
const STANDAARD_MAANDEN = 3;

export interface WeekMaandTabsProps {
  /** Wiens overzicht de tab Maand toont: het eigen, of uitsluitend dat van de collega. */
  bron: TrendBron;
  /** De inhoud van de tab Week, met de weekkiezer: die staat op Maand dus niet in beeld. */
  week: ReactNode;
  /** Het kopniveau binnen de tab Maand; 3 binnen het Zo-ziet-kader. */
  kopNiveau?: 2 | 3 | 4;
  /** De naam van de tablist; standaard `qa.eigen.tabLijstAria`. Het Zo-ziet-kader noemt de medewerker. */
  tabsLabel?: string;
}

/**
 * De tabs **Week** (standaard) en **Maand** van "Mijn feedback" en van het Zo-ziet-kader
 * (ux-spec 2, SPEC-meerweekenoverzicht-feedback CAP-7).
 *
 * De tabs en de gekozen periode leven hier, niet in het adres: de week in het adres blijft wat hij was, en
 * de periode blijft staan bij wisselen van tab. Maand wordt pas gerenderd als de tab open is, zodat het
 * overzicht niet wordt opgevraagd zolang niemand ernaar kijkt.
 */
export function WeekMaandTabs({ bron, week, kopNiveau = 2, tabsLabel }: WeekMaandTabsProps) {
  const { t } = useTranslation();
  const [weergave, setWeergave] = useState<Weergave>('week');
  const [maanden, setMaanden] = useState(STANDAARD_MAANDEN);

  /** Pijltoetsen links en rechts wisselen van tab, en de focus gaat mee (ux-spec 7). */
  function toets(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    // Twee tabs: een pijltoets wisselt naar de andere, links en rechts.
    const volgende: Weergave = weergave === 'week' ? 'maand' : 'week';
    setWeergave(volgende);
    document.getElementById(`werkkwaliteit-tab-${volgende}`)?.focus();
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div aria-label={tabsLabel ?? t('qa.eigen.tabLijstAria')} className="flex gap-1 border-b border-[var(--border)]" role="tablist">
        {WEERGAVEN.map((naam) => {
          const actief = weergave === naam;
          return (
            <button
              aria-controls={`werkkwaliteit-paneel-${naam}`}
              aria-selected={actief}
              className={`border-b-[3px] px-3.5 py-2.5 text-[15px] font-semibold ${
                actief ? 'border-[var(--primary)] text-[var(--primary)]' : 'border-transparent text-[var(--foreground-muted)]'
              } focus-visible:outline-2`}
              id={`werkkwaliteit-tab-${naam}`}
              key={naam}
              onClick={() => setWeergave(naam)}
              onKeyDown={toets}
              role="tab"
              tabIndex={actief ? 0 : -1}
              type="button"
            >
              {t(naam === 'week' ? 'qa.eigen.tabWeek' : 'qa.eigen.tabMaand')}
            </button>
          );
        })}
      </div>

      <div
        aria-labelledby="werkkwaliteit-tab-week"
        className="flex min-w-0 flex-col gap-4"
        data-testid="werkkwaliteit-paneel-week"
        hidden={weergave !== 'week'}
        id="werkkwaliteit-paneel-week"
        role="tabpanel"
      >
        {week}
      </div>

      <div
        aria-labelledby="werkkwaliteit-tab-maand"
        className="flex min-w-0 flex-col gap-4"
        data-testid="werkkwaliteit-paneel-maand"
        hidden={weergave !== 'maand'}
        id="werkkwaliteit-paneel-maand"
        role="tabpanel"
      >
        {weergave === 'maand' && <MaandOverzicht bron={bron} kopNiveau={kopNiveau} maanden={maanden} onKiesMaanden={setMaanden} />}
      </div>
    </div>
  );
}
